import {
  useState,
  createContext,
  useContext,
  Dispatch,
  SetStateAction
} from 'react'
import { ApolloQueryResult, DocumentNode } from '@apollo/client'
import dayjs, { Dayjs } from 'dayjs'
import { usePagination } from 'hooks/usePagination'
import {
  LoyaltyCustomer,
  LoyaltyCustomerListItem,
  SortBy,
  SortDirection
} from 'components/Loyalty/types'
import { LoyaltySource } from 'components/Loyalty/utils'
import { QUERY_LOYALTY_CUSTOMERS_AGGREGATES } from 'components/Loyalty/LoyaltyQueries'

type RefetchFn =
  | ((variables?: Partial<Record<string, unknown>>) => Promise<any>)
  | undefined

// Shared shape for loadLoyaltyCustomersPage/loadLoyaltyCustomersExportPage —
// both are useLazyQuery execute functions handed through Context so
// LoyaltyOverviewLoader can own the query while LoyaltyOverview.tsx calls it.
type PageLoaderFn = (options: {
  variables: Record<string, unknown>
}) => Promise<any>
const noopPageLoader: PageLoaderFn = () =>
  Promise.resolve({} as ApolloQueryResult<any>)

export type LoyaltyOverviewState = {
  startDate: Dayjs
  setStartDate: (date: Dayjs) => void
  endDate: Dayjs
  setEndDate: (date: Dayjs) => void
  storeIds: string[]
  setStoreIds: (ids: string[]) => void
  optInFilter: boolean
  setOptInFilter: (value: boolean) => void
  activeSort: string
  setActiveSort: (value: string) => void
  sortBy: SortBy
  setSortBy: (value: SortBy) => void
  sortDirection: SortDirection
  setSortDirection: (value: SortDirection) => void
  currentPage: number
  setCurrentPage: (page: number) => void
  pageSize: number
  setPageSize: (pageSize: number) => void
  resetPage: () => void
  getOffset: (page: number, pageSize: number) => number
  customers: LoyaltyCustomer[]
  setCustomers: (customers: LoyaltyCustomer[]) => void
  // From getLoyaltyCustomersListCount — same membership logic as the list
  // itself, so this can never disagree with what the table actually shows.
  customersTotalCount: number
  setCustomersTotalCount: (count: number) => void
  customersTotalCountLoading: boolean
  setCustomersTotalCountLoading: (value: boolean) => void
  loyaltyCustomersList: LoyaltyCustomerListItem[]
  setLoyaltyCustomersList: (list: LoyaltyCustomerListItem[]) => void
  paginatedCustomersList: LoyaltyCustomerListItem[]
  setPaginatedCustomersList: (list: LoyaltyCustomerListItem[]) => void
  customerAggregateQuery: DocumentNode
  setCustomerAggregateQuery: (query: DocumentNode) => void
  selectedDropdownOption: string
  setSelectedDropdownOption: (value: string) => void
  loyaltySource: LoyaltySource
  setLoyaltySource: (value: LoyaltySource) => void
  hasLoadedOverviewData: boolean
  setHasLoadedOverviewData: (value: boolean) => void
  // Populated by LoyaltyOverviewLoader (src/router/Loaders/LoyaltyOverviewLoader.tsx)
  // — the query trigger functions/loading flags can't be declared here, since
  // this Context holds state only, no fetching — see doc/ARCHITECTURE.md §6.1.
  loadCustomersAggregate: () => void
  setLoadCustomersAggregate: Dispatch<SetStateAction<() => void>>
  loyaltyCustomersAggregateRefetch: RefetchFn
  setLoyaltyCustomersAggregateRefetch: Dispatch<SetStateAction<RefetchFn>>
  loyaltyCustomersAggregateLoading: boolean
  setLoyaltyCustomersAggregateLoading: (value: boolean) => void
  refetchLoyaltyCustomersList: RefetchFn
  setRefetchLoyaltyCustomersList: Dispatch<SetStateAction<RefetchFn>>
  loyaltyCustomersListLoading: boolean
  setLoyaltyCustomersListLoading: (value: boolean) => void
  loadLoyaltyCustomersPage: PageLoaderFn
  setLoadLoyaltyCustomersPage: Dispatch<SetStateAction<PageLoaderFn>>
  loyaltyCustomersPageLoading: boolean
  setLoyaltyCustomersPageLoading: (value: boolean) => void
  // Used by the CSV export flow to page through the full customer set
  // on demand (LoyaltyOverview.tsx's handleExport) — see LoyaltyOverviewLoader.
  loadLoyaltyCustomersExportPage: PageLoaderFn
  setLoadLoyaltyCustomersExportPage: Dispatch<SetStateAction<PageLoaderFn>>
}

export const SetupLoyaltyOverview = (): LoyaltyOverviewState => {
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(7, 'days'))
  const [endDate, setEndDate] = useState<Dayjs>(dayjs())
  const [storeIds, setStoreIds] = useState<string[]>([])
  const [optInFilter, setOptInFilter] = useState(false)
  const [activeSort, setActiveSort] = useState('total-spent-desc')
  const [sortBy, setSortBy] = useState<SortBy>('orders')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    resetPage,
    getOffset
  } = usePagination()
  const [customers, setCustomers] = useState<LoyaltyCustomer[]>([])
  const [customersTotalCount, setCustomersTotalCount] = useState(0)
  const [customersTotalCountLoading, setCustomersTotalCountLoading] =
    useState(false)
  const [loyaltyCustomersList, setLoyaltyCustomersList] = useState<
    LoyaltyCustomerListItem[]
  >([])
  const [paginatedCustomersList, setPaginatedCustomersList] = useState<
    LoyaltyCustomerListItem[]
  >([])
  const [customerAggregateQuery, setCustomerAggregateQuery] =
    useState<DocumentNode>(QUERY_LOYALTY_CUSTOMERS_AGGREGATES)
  const [selectedDropdownOption, setSelectedDropdownOption] =
    useState<string>('all_stores')
  const [loyaltySource, setLoyaltySource] = useState<LoyaltySource>(undefined)
  const [hasLoadedOverviewData, setHasLoadedOverviewData] = useState(false)

  const [loadCustomersAggregate, setLoadCustomersAggregate] = useState<
    () => void
  >(() => () => undefined)
  const [
    loyaltyCustomersAggregateRefetch,
    setLoyaltyCustomersAggregateRefetch
  ] = useState<RefetchFn>(undefined)
  const [
    loyaltyCustomersAggregateLoading,
    setLoyaltyCustomersAggregateLoading
  ] = useState(false)
  const [refetchLoyaltyCustomersList, setRefetchLoyaltyCustomersList] =
    useState<RefetchFn>(undefined)
  const [loyaltyCustomersListLoading, setLoyaltyCustomersListLoading] =
    useState(false)
  const [loadLoyaltyCustomersPage, setLoadLoyaltyCustomersPage] =
    useState<PageLoaderFn>(() => noopPageLoader)
  const [loyaltyCustomersPageLoading, setLoyaltyCustomersPageLoading] =
    useState(false)
  const [loadLoyaltyCustomersExportPage, setLoadLoyaltyCustomersExportPage] =
    useState<PageLoaderFn>(() => noopPageLoader)

  return {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    storeIds,
    setStoreIds,
    optInFilter,
    setOptInFilter,
    activeSort,
    setActiveSort,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    resetPage,
    getOffset,
    customers,
    setCustomers,
    customersTotalCount,
    setCustomersTotalCount,
    customersTotalCountLoading,
    setCustomersTotalCountLoading,
    loyaltyCustomersList,
    setLoyaltyCustomersList,
    paginatedCustomersList,
    setPaginatedCustomersList,
    customerAggregateQuery,
    setCustomerAggregateQuery,
    selectedDropdownOption,
    setSelectedDropdownOption,
    loyaltySource,
    setLoyaltySource,
    hasLoadedOverviewData,
    setHasLoadedOverviewData,
    loadCustomersAggregate,
    setLoadCustomersAggregate,
    loyaltyCustomersAggregateRefetch,
    setLoyaltyCustomersAggregateRefetch,
    loyaltyCustomersAggregateLoading,
    setLoyaltyCustomersAggregateLoading,
    refetchLoyaltyCustomersList,
    setRefetchLoyaltyCustomersList,
    loyaltyCustomersListLoading,
    setLoyaltyCustomersListLoading,
    loadLoyaltyCustomersPage,
    setLoadLoyaltyCustomersPage,
    loyaltyCustomersPageLoading,
    setLoyaltyCustomersPageLoading,
    loadLoyaltyCustomersExportPage,
    setLoadLoyaltyCustomersExportPage
  }
}

const initialState: LoyaltyOverviewState = {
  startDate: dayjs(),
  setStartDate: () => null,
  endDate: dayjs(),
  setEndDate: () => null,
  storeIds: [],
  setStoreIds: () => null,
  optInFilter: false,
  setOptInFilter: () => null,
  activeSort: 'total-spent-desc',
  setActiveSort: () => null,
  sortBy: 'orders',
  setSortBy: () => null,
  sortDirection: 'desc',
  setSortDirection: () => null,
  currentPage: 1,
  setCurrentPage: () => null,
  pageSize: 100,
  setPageSize: () => null,
  resetPage: () => null,
  getOffset: (page: number, pageSize: number) => (page - 1) * pageSize,
  customers: [],
  setCustomers: () => null,
  customersTotalCount: 0,
  setCustomersTotalCount: () => null,
  customersTotalCountLoading: false,
  setCustomersTotalCountLoading: () => null,
  loyaltyCustomersList: [],
  setLoyaltyCustomersList: () => null,
  paginatedCustomersList: [],
  setPaginatedCustomersList: () => null,
  customerAggregateQuery: QUERY_LOYALTY_CUSTOMERS_AGGREGATES,
  setCustomerAggregateQuery: () => null,
  selectedDropdownOption: 'all_stores',
  setSelectedDropdownOption: () => null,
  loyaltySource: undefined,
  setLoyaltySource: () => null,
  hasLoadedOverviewData: false,
  setHasLoadedOverviewData: () => null,
  loadCustomersAggregate: () => null,
  setLoadCustomersAggregate: () => null,
  loyaltyCustomersAggregateRefetch: undefined,
  setLoyaltyCustomersAggregateRefetch: () => null,
  loyaltyCustomersAggregateLoading: false,
  setLoyaltyCustomersAggregateLoading: () => null,
  refetchLoyaltyCustomersList: undefined,
  setRefetchLoyaltyCustomersList: () => null,
  loyaltyCustomersListLoading: false,
  setLoyaltyCustomersListLoading: () => null,
  loadLoyaltyCustomersPage: noopPageLoader,
  setLoadLoyaltyCustomersPage: () => null,
  loyaltyCustomersPageLoading: false,
  setLoyaltyCustomersPageLoading: () => null,
  loadLoyaltyCustomersExportPage: noopPageLoader,
  setLoadLoyaltyCustomersExportPage: () => null
}

export const LoyaltyOverviewContext =
  createContext<LoyaltyOverviewState>(initialState)

export const useLoyaltyOverview = () => useContext(LoyaltyOverviewContext)
